import type { StorageKeys } from "@/types/web-storage"
import { Honeybadger } from "@honeybadger-io/react"
import { v4 as uuidv4 } from "uuid"

import { isSafeCallbackUrl } from "@/lib/auth-utils"
import { getBrowserCapabilities } from "@/lib/auth/browser-capabilities"
import { nowMillis } from "@/lib/date-utils"
import { isClientSide } from "@/lib/is-client-side"
import { mtLocation } from "@/lib/mt-location"
import { webStorage } from "@/lib/web-storage"

export type RedirectPage = "signin" | "refresh" | "signout" | "create-anon"

const MAX_REDIRECTS_PER_TAB = 5 // Max auth page visits per tab in time window before loop detected
const TIME_WINDOW_MS = 30 * 1000 // 30 seconds

interface AuthVisit {
  ts: number // Timestamp of the visit
  tabId: string // Tab that made the visit
}

interface AuthLoopRecord {
  // Array of visits within the time window - no explicit reset needed
  visits: AuthVisit[]
}

const SHARED_KEY: StorageKeys = "auth.redirect.loop.shared"
const TAB_ID_KEY: StorageKeys = "auth.redirect.tab.id"

// Generate or get tab ID for per-tab tracking
function getTabId(): string {
  if (!isClientSide()) return "unknown"

  // Try to get existing tab ID from sessionStorage (persists per tab)
  let tabId = webStorage.getItem(TAB_ID_KEY, { type: "session" })
  if (!tabId) {
    // Generate new tab ID using UUID
    tabId = uuidv4()
    webStorage.setItem(TAB_ID_KEY, tabId, { type: "session" })
  }
  return tabId
}

function getRecord(): AuthLoopRecord | null {
  if (!isClientSide()) return null

  try {
    // Try localStorage first (survives page reloads)
    const storageType = "local"
    return webStorage.tryGetJSON<AuthLoopRecord>(SHARED_KEY, {
      type: storageType,
    })
  } catch {
    return null
  }
}

function setRecord(record: AuthLoopRecord): void {
  if (!isClientSide()) return

  try {
    // Try localStorage first (survives page reloads)
    const storageType = "local"
    webStorage.setJSON(SHARED_KEY, record, { type: storageType })
  } catch (error) {
    console.error("[AuthLoopDetector] Failed to set record:", error)
  }
}

function detectNestedAuthCallbackUrl(callbackUrl?: string | null): boolean {
  // Use the shared utility - invert the result since this function checks for nested (unsafe) URLs
  return !isSafeCallbackUrl(callbackUrl)
}

export type AuthLoopResult =
  | { reason: "normal"; count: number }
  | { reason: "capability" }
  | { reason: "redirect-loop"; count: number }

interface AuthLoopContext {
  callbackUrl?: string | null
  referer?: string
  presentCookies?: string[]
  serverError?: string
  redirectUrl?: string
  createAnon?: boolean
}

export const authLoopDetector = {
  checkAndIncrement(
    page: RedirectPage,
    context?: AuthLoopContext
  ): AuthLoopResult {
    if (!isClientSide()) {
      return { count: 0, reason: "normal" }
    }

    const capabilities = getBrowserCapabilities()
    const capabilityFailure =
      !capabilities.cookiesEnabled ||
      (!capabilities.localStorageAvailable &&
        !capabilities.sessionStorageAvailable)

    if (capabilityFailure) {
      Honeybadger.notify(
        new Error(`Browser capability unavailable on ${page}`),
        {
          context: {
            page,
            ...capabilities,
            presentCookies: context?.presentCookies,
            currentUrl: mtLocation.href,
            referrer: context?.referer,
          },
        }
      )
      mtLocation.replace("/check-settings")
      return { reason: "capability" }
    }

    const callbackUrl = context?.callbackUrl ?? undefined
    const hasNestedAuth = detectNestedAuthCallbackUrl(callbackUrl)
    if (hasNestedAuth) {
      console.error(
        `[AuthLoopDetector] Nested auth callbackUrl detected on ${page}`
      )

      Honeybadger.notify(
        new Error(`Nested auth callbackUrl detected on ${page}`),
        {
          context: {
            page,
            callbackUrl,
            currentUrl: mtLocation.href,
            ...context,
          },
        }
      )

      return { count: 0, reason: "redirect-loop" }
    }

    const now = nowMillis()
    const record = getRecord()
    const tabId = getTabId()

    // Filter out old visits and add current visit
    const recentVisits = (record?.visits ?? []).filter(
      (v) => now - v.ts <= TIME_WINDOW_MS
    )
    recentVisits.push({ ts: now, tabId })

    // Save updated record (keeps all tab visits for diagnostics)
    setRecord({ visits: recentVisits })

    // Count only visits from this tab for loop detection
    const tabVisits = recentVisits.filter((v) => v.tabId === tabId)
    const count = tabVisits.length

    // Check if we've hit the max redirects threshold for this tab
    if (count >= MAX_REDIRECTS_PER_TAB) {
      console.error(
        `[AuthLoopDetector] Auth redirect loop detected (${count}/${MAX_REDIRECTS_PER_TAB})`
      )

      Honeybadger.notify(new Error(`Auth redirect loop detected on ${page}`), {
        context: {
          page,
          redirectCount: count,
          maxRedirects: MAX_REDIRECTS_PER_TAB,
          cookiesEnabled: true,
          tabVisits,
          allRecentVisits: recentVisits,
          ...context,
        },
      })

      return { count, reason: "redirect-loop" }
    }

    return { count, reason: "normal" }
  },
}
